import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Phone } from 'lucide-react';

const CtaSection = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setInView(true),
      { threshold: 0.15 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  // Subtle parallax on scroll
  useEffect(() => {
    const onScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const wh = window.innerHeight;
      // -ve when section is above center, +ve below
      const progress = (wh / 2 - (rect.top + rect.height / 2)) / wh;
      setOffset(progress * 60);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section
      ref={ref}
      id="cta"
      className="relative w-full min-h-[100vh] overflow-hidden flex items-center justify-center"
    >
      {/* Background image with parallax + Ken Burns zoom */}
      <div
        className="absolute inset-0 will-change-transform"
        style={{ transform: `translate3d(0, ${offset}px, 0)` }}
      >
        <img
          src="https://images.unsplash.com/photo-1561848355-890d054dc55a?w=2200&q=85"
          alt="Soft blush roses"
          className={`w-full h-[120%] object-cover transition-transform duration-[6000ms] ease-out ${
            inView ? 'scale-105' : 'scale-110'
          }`}
        />
        {/* Cinematic overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/70" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(0,0,0,0.0)_0%,_rgba(0,0,0,0.55)_85%)]" />
        {/* Subtle gold film grain shimmer */}
        <div
          className="absolute inset-0 opacity-[0.07] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
          }}
        />
      </div>

      {/* Top & bottom letterbox edges for cinematic feel */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-[#0a0a0a] to-transparent z-[1]" />
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0a0a0a] to-transparent z-[1]" />

      {/* Corner ornaments */}
      <span className="hidden md:block absolute top-14 left-14 w-20 h-20 border-t border-l border-[#c9a96e]/60 z-[2]" />
      <span className="hidden md:block absolute top-14 right-14 w-20 h-20 border-t border-r border-[#c9a96e]/60 z-[2]" />
      <span className="hidden md:block absolute bottom-14 left-14 w-20 h-20 border-b border-l border-[#c9a96e]/60 z-[2]" />
      <span className="hidden md:block absolute bottom-14 right-14 w-20 h-20 border-b border-r border-[#c9a96e]/60 z-[2]" />

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
        <p
          className={`text-[#c9a96e] text-[11px] tracking-[0.55em] uppercase mb-8 transition-all duration-1000 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          Begin Your Story
        </p>

        {/* Decorative hairline divider with diamond */}
        <div
          className={`flex items-center justify-center gap-4 mb-10 transition-all duration-1000 delay-150 ${
            inView ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          <span className="h-px w-12 bg-[#c9a96e]/60" />
          <span className="w-1.5 h-1.5 rotate-45 bg-[#c9a96e]" />
          <span className="h-px w-12 bg-[#c9a96e]/60" />
        </div>

        <h2
          className={`font-serif text-white text-5xl md:text-6xl lg:text-7xl leading-[1.05] transition-all duration-[1200ms] delay-200 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          Ready to start planning
        </h2>
        <h2
          className={`font-serif italic text-[#e8c98a] text-5xl md:text-6xl lg:text-7xl leading-[1.05] mt-2 transition-all duration-[1200ms] delay-300 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          your perfect day?
        </h2>

        <p
          className={`mt-10 text-white/85 text-base md:text-lg leading-[1.9] font-light max-w-xl mx-auto transition-all duration-1000 delay-500 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          We work with a select number of couples each year.
          <br className="hidden sm:block" /> Reach out to check availability.
        </p>

        <div
          className={`mt-14 flex flex-col sm:flex-row gap-5 justify-center items-stretch sm:items-center transition-all duration-1000 delay-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <a
            href="#contact"
            className="group relative overflow-hidden bg-[#e8d5a8] text-black px-10 py-5 text-[11px] tracking-[0.35em] uppercase inline-flex items-center justify-center gap-3"
          >
            <span className="absolute inset-0 bg-[#c9a96e] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
            <span className="relative z-10">Start the Conversation</span>
            <ArrowRight
              size={14}
              className="relative z-10 group-hover:translate-x-1 transition-transform"
            />
          </a>

          <a
            href="tel:+18882992250"
            className="group border border-white/50 hover:border-[#c9a96e] hover:text-[#c9a96e] text-white px-10 py-5 text-[11px] tracking-[0.35em] uppercase inline-flex items-center justify-center gap-3 transition-colors duration-300 backdrop-blur-[2px]"
          >
            <Phone size={14} strokeWidth={1.5} />
            1-888-299-2250
          </a>
        </div>
      </div>
    </section>
  );
};

export default CtaSection;
